import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/highcharts-3d";

const chart_verticals = [
  { title: "Real Estate and Rental and Leasing", gdp: 41.67 },
  { title: "Government (Public Administration)", gdp: 31.36 },
  { title: "Manufacturing", gdp: 29.06 },
  { title: "— Aerospace & Defense (part of Manufacturing)", gdp: 4.43 },
  { title: "— Food Industry (part of Manufacturing)", gdp: 2.74 },
  { title: "Finance and Insurance", gdp: 26.63 },
  { title: "Construction", gdp: 23.78 },
  { title: "Professional, Scientific, and Technical Services", gdp: 22.73 },
  { title: "Retail Trade", gdp: 21.94 },
  { title: "Healthcare and Social Assistance", gdp: 16.68 },
  { title: "Wholesale Trade", gdp: 14.68 },
  { title: "Information", gdp: 14.5 },
  { title: "Transportation and Warehousing", gdp: 9.88 },
  { title: "Utilities", gdp: 6.81 },
  { title: "Mining", gdp: 4.69 },
  { title: "Arts, Entertainment, and Recreation", gdp: 2.73 },
];

const COLORS = [
  "#fa9a25",
  "#95a5a6",
  "#5d6d7e",
  "#7FB3D5",
  "#EC7063",
  "#fadbd8",
  "#96F7E4",
  "#014CB2",
  "#F4A8FF",
  "#7b241c",
  "#196f3d",
  "#f7dc65",
  "#f97316",
  "#a2d9ce",
  "#935116",
  "#17a589",
];

export default function GDP3DPieChart() {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth || 0);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isCompact = containerWidth > 0 && containerWidth < 760;

  // Calculate total and percent data
  const totalGDP = useMemo(
    () => chart_verticals.reduce((sum, v) => sum + v.gdp, 0),
    []
  );

  const dataWithPercent = useMemo(
    () =>
      chart_verticals.map((v, idx) => ({
        name: v.title,
        y: (v.gdp / totalGDP) * 100,
        dataLabels: {
          enabled: !isCompact || activeIndex === idx,
        },
      })),
    [totalGDP, isCompact, activeIndex]
  );

  const options = {
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      options3d: {
        enabled: true,
        alpha: 45,
        beta: 0,
      },
    },
    title: {
      text: "GDP Share Percentage by Sector",
      style: { fontSize: "20px", fontWeight: "bold", color: "#111" },
    },
    tooltip: {
      pointFormat: "<b>{point.y:.2f}%</b> of total GDP",
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        depth: 35,
        colors: COLORS,
        dataLabels: {
          enabled: false, // point-level enables labels selectively
          format: "{point.name}: {point.y:.2f}%",
          style: {
            fontSize: "13px",
            fontWeight: "500",
            color: "#000",
          },
          allowOverlap: false,
          distance: isCompact ? 10 : 22,
          connectorPadding: 6,
          connectorWidth: 1,
          softConnector: true,
          crop: false,           // keep labels outside instead of clipping
          overflow: "justify",
        },
        point: {
          events: {
            click: function () {
              // Toggle label visibility for the clicked slice in compact mode
              if (isCompact) {
                setActiveIndex((prev) =>
                  prev === this.index ? null : this.index
                );
              }
            },
          },
        },
      },
    },
    series: [
      {
        type: "pie",
        name: "GDP Share (%)",
        data: dataWithPercent,
      },
    ],
    credits: { enabled: false },
  };

  return (
    <div ref={containerRef}>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
}